/**
 * PlaceCard — Editorial place card for the personal guidebook.
 * Two-column on desktop (photo panel + content), single-column on mobile.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { BREAKPOINTS } from "@/lib/breakpoints";
import "./PlaceCard.css";

const FONT_LINK_ID = "playfair-display-font";

function ensureFont() {
  if (typeof document === "undefined") return;
  if (document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap";
  link.crossOrigin = "anonymous";
  document.head.appendChild(link);
}

export interface PlaceCardPhoto {
  url: string;
  alt?: string;
}

export interface PlaceCardProps {
  name: string;
  category?: string;
  priceLevel?: string;
  rating?: number;
  reviewCount?: number;
  isOpen?: boolean;
  description?: string;
  neighborhood?: string;
  photos: PlaceCardPhoto[];
  note?: string;
  userRating?: number;
  ratingSaved?: boolean;
  onNoteChange: (note: string) => void | Promise<void>;
  onRatingChange?: (rating: number) => void;
  onDirectionsClick?: () => void;
  onWebsiteClick?: () => void;
  onCallClick?: () => void;
  onPhotoClick?: (index: number) => void; // deprecated — no lightbox in this version
}

const SEP = <span style={{ opacity: 0.3, margin: "0 6px" }}>&middot;</span>;

export default function PlaceCard({
  name,
  category,
  priceLevel,
  rating,
  reviewCount,
  isOpen,
  description,
  neighborhood,
  photos,
  note,
  userRating,
  ratingSaved,
  onNoteChange,
  onRatingChange,
  onDirectionsClick,
  onWebsiteClick,
  onCallClick,
  onPhotoClick,
}: PlaceCardProps) {
  useEffect(() => {
    ensureFont();
  }, []);

  const [activePhoto, setActivePhoto] = useState(0);
  const [noteValue, setNoteValue] = useState(note || "");
  const [noteDirty, setNoteDirty] = useState(false);
  const [noteFocused, setNoteFocused] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [noteError, setNoteError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const stripRef = useRef<HTMLDivElement>(null);

  // Only sync external note prop on initial load or when it changes externally.
  const noteRef = useRef(note || "");
  // Latest textarea value — lets an async save's resolution check whether the
  // user kept editing while the save was in flight (stale "Saved" guard).
  const latestNoteRef = useRef(note || "");
  useEffect(() => {
    if (note !== undefined && note !== noteRef.current) {
      noteRef.current = note || "";
      latestNoteRef.current = note || "";
      setNoteValue(note || "");
    }
  }, [note]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= BREAKPOINTS.compactMax);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const handleNoteInput = useCallback((val: string) => {
    latestNoteRef.current = val;
    setNoteValue(val);
    setNoteSaved(false);
    setNoteError(false);
  }, []);

  const handleNoteDone = useCallback(() => {
    setNoteError(false);
    const savedValue = noteValue;
    const markSaved = () => {
      // A slow save may resolve after the user resumed typing; only re-assert
      // "Saved" if the textarea still holds the exact value that was saved —
      // otherwise we'd show a green "Saved" over newer unsaved edits.
      if (latestNoteRef.current !== savedValue) return;
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 1500);
    };
    try {
      const result = onNoteChange(noteValue);
      if (result && typeof result.then === "function") {
        result.then(markSaved).catch((e: unknown) => {
          console.error("[PlaceCard] onNoteChange rejected:", e);
          setNoteError(true);
        });
      } else {
        markSaved();
      }
    } catch (e) {
      console.error("[PlaceCard] onNoteChange threw:", e);
      setNoteError(true);
    }
  }, [noteValue, onNoteChange]);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const thumb = strip.children[activePhoto] as HTMLElement;
    if (thumb)
      thumb.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
  }, [activePhoto]);

  const hasActions = onDirectionsClick || onWebsiteClick || onCallClick;
  // Track photo URLs that failed to load so we can drop the (otherwise black)
  // photo panel instead of showing an empty/broken hero. A place with no usable
  // photo (e.g. an un-enriched card) renders content-only, single column.
  const [failedPhotos, setFailedPhotos] = useState<Record<string, boolean>>({});
  const usablePhotos = photos.filter((p) => p.url && !failedPhotos[p.url]);
  const showPhotoPanel = usablePhotos.length > 0;
  // Hero source: the active photo if it's still usable, else fall back to the
  // first usable one — otherwise a failed *active* photo would render broken in
  // the hero even though the panel stays open because OTHER photos are usable.
  const activeCandidate = photos[activePhoto];
  const currentPhotoUrl =
    (activeCandidate &&
      !failedPhotos[activeCandidate.url] &&
      activeCandidate.url) ||
    usablePhotos[0]?.url ||
    "";

  return (
    <div
      onTouchMove={(e) => e.stopPropagation()}
      style={{
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: isMobile || !showPhotoPanel ? "1fr" : "1fr 1fr",
        background: "var(--color-surface, #fff)",
        borderRadius: "var(--border-radius-lg, 12px)",
        overscrollBehavior: "contain",
      }}
    >
      {/* ── Photo Panel (hidden when there's no usable photo — avoids a black box) ── */}
      {showPhotoPanel && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            background: "#111",
          }}
        >
          <div
            style={{
              aspectRatio: "1 / 1",
              width: "100%",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {currentPhotoUrl ? (
              <img
                key={currentPhotoUrl}
                src={currentPhotoUrl}
                alt={photos[activePhoto]?.alt || name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  pointerEvents: "none",
                }}
                onError={() =>
                  setFailedPhotos((f) => ({ ...f, [currentPhotoUrl]: true }))
                }
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(255,255,255,0.5)"
                  strokeWidth="1.5"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="m21 15-5-5L5 21" />
                </svg>
              </div>
            )}
          </div>

          <div
            ref={stripRef}
            className="place-card-thumb-strip"
            style={{
              display: "flex",
              gap: "1px",
              height: "86px",
              overflowX: "auto",
              overflowY: "hidden",
              scrollSnapType: "x mandatory",
              flexShrink: 0,
              scrollbarWidth: "none",
              overscrollBehavior: "contain",
              touchAction: "pan-x",
            }}
          >
            {photos.map((photo, idx) => (
              <button
                key={idx}
                onClick={() => setActivePhoto(idx)}
                style={{
                  flex: "0 0 86px",
                  height: "100%",
                  aspectRatio: "1 / 1",
                  padding: 0,
                  border: "none",
                  background: "#222",
                  cursor: "pointer",
                  scrollSnapAlign: "start",
                  opacity: idx === activePhoto ? 1 : 0.7,
                  outline: idx === activePhoto ? "2px solid white" : "none",
                  outlineOffset: "-2px",
                  transition: "opacity 0.15s",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  if (idx !== activePhoto) e.currentTarget.style.opacity = "1";
                }}
                onMouseLeave={(e) => {
                  if (idx !== activePhoto)
                    e.currentTarget.style.opacity = "0.7";
                }}
              >
                {photo.url ? (
                  <img
                    src={photo.url}
                    alt={photo.alt || `Photo ${idx + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="rgba(255,255,255,0.4)"
                      strokeWidth="1.5"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="m21 15-5-5L5 21" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className="xv-scroll"
        style={{
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          overflow: "auto",
          padding: isMobile ? "1.2rem 1.3rem" : "1.8rem 2.1rem",
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-serif)",
            fontSize: isMobile ? "20px" : "26px",
            fontWeight: 400,
            letterSpacing: "-0.3px",
            color: "var(--color-text, #1a1a1a)",
            margin: "0 0 5px",
            lineHeight: 1.2,
            paddingRight: "48px",
          }}
        >
          {name}
        </h2>

        <div
          style={{
            fontSize: "14px",
            color: "var(--color-text-muted, #4a4d51)",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "0",
            marginBottom: "10px",
          }}
        >
          {category && <>{category}</>}
          {priceLevel && (
            <>
              {SEP}
              {priceLevel}
            </>
          )}
          {isOpen != null && (
            <>
              {SEP}
              <span style={{ color: isOpen ? "#2a8a60" : "#c0533a" }}>
                {isOpen ? "Open now" : "Closed now"}
              </span>
            </>
          )}
        </div>

        {description && (
          <p
            style={{
              fontFamily: "var(--font-serif)",
              fontSize: "14.5px",
              lineHeight: 1.6,
              color: "var(--color-text-muted, #4a4d51)",
              margin: "0 0 1rem",
            }}
          >
            {description}
          </p>
        )}

        {onRatingChange && (
          <div style={{ marginBottom: "0.9rem" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <label
                style={{
                  fontSize: "12px",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--color-text-faint, #7a7f85)",
                }}
              >
                Your rating
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {userRating ? (
                  <span
                    style={{
                      fontSize: 18,
                      fontWeight: 500,
                      color: "var(--color-text)",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    {userRating.toFixed(1)}
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: 13,
                      color: "var(--color-text-muted, #999)",
                    }}
                  >
                    Not rated
                  </span>
                )}
                {ratingSaved && (
                  <span
                    style={{ fontSize: 12, color: "#2a8a60", fontWeight: 500 }}
                  >
                    Saved
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  fontSize: 11,
                  color: "var(--color-text-muted, #999)",
                  flexShrink: 0,
                }}
              >
                0
              </span>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={userRating ? Math.round(userRating * 10) : 0}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10) / 10;
                  onRatingChange(val);
                }}
                style={{
                  flex: 1,
                  height: 4,
                  appearance: "none",
                  WebkitAppearance: "none",
                  background: `linear-gradient(to right, #1a9ba5 ${((userRating ?? 0) / 10) * 100}%, var(--color-surface-muted, #e0e0e0) ${((userRating ?? 0) / 10) * 100}%)`,
                  borderRadius: 2,
                  outline: "none",
                  cursor: "pointer",
                }}
              />
              <span
                style={{
                  fontSize: 11,
                  color: "var(--color-text-muted, #999)",
                  flexShrink: 0,
                }}
              >
                10
              </span>
            </div>
            {userRating && (
              <button
                onClick={() => onRatingChange(0)}
                style={{
                  fontSize: 11,
                  color: "var(--color-text-muted, #999)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px 0",
                  marginTop: 4,
                }}
              >
                Clear rating
              </button>
            )}
          </div>
        )}

        <div
          style={{
            height: "0.5px",
            background: "var(--color-border-faint, #f1f3f4)",
            margin: "0 0 0.9rem",
          }}
        />

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            marginBottom: "0.9rem",
          }}
        >
          <label
            style={{
              fontSize: "12px",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--color-text-faint, #7a7f85)",
              display: "block",
              marginBottom: "6px",
            }}
          >
            Your note
          </label>
          <textarea
            value={noteValue}
            onChange={(e) => handleNoteInput(e.target.value)}
            placeholder={
              "Add something personal \u2014 a memory, a tip, a reason to return\u2026"
            }
            rows={isMobile ? 4 : 3}
            style={{
              width: "100%",
              flex: 1,
              minHeight: 0,
              boxSizing: "border-box",
              fontFamily: "var(--font-sans, inherit)",
              fontSize: "15px",
              lineHeight: 1.6,
              color: "var(--color-text, #1a1a1a)",
              background: "var(--color-surface-muted, #f8f9fa)",
              border: "0.5px solid var(--color-border-faint, #f1f3f4)",
              borderRadius: "var(--border-radius-md, 8px)",
              padding: "8px 10px",
              resize: "none",
              outline: "none",
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor =
                "var(--color-border, #dadce0)";
              setNoteFocused(true);
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor =
                "var(--color-border-faint, #f1f3f4)";
              setTimeout(() => setNoteFocused(false), 200);
            }}
          />
          {(noteValue !== (note || "") || noteSaved) && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "6px",
              }}
            >
              {noteSaved ? (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#2a8a60",
                    fontWeight: 500,
                  }}
                >
                  Saved
                </span>
              ) : (
                <>
                  {noteError && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "var(--color-danger, #EA4335)",
                        fontWeight: 500,
                        marginRight: "10px",
                        alignSelf: "center",
                      }}
                    >
                      Couldn&rsquo;t save
                    </span>
                  )}
                  <button
                    onClick={handleNoteDone}
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "white",
                      background: "var(--color-primary, #3C4043)",
                      border: "none",
                      borderRadius: "6px",
                      padding: "4px 14px",
                      cursor: "pointer",
                    }}
                  >
                    Done
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {hasActions && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              paddingTop: "0.85rem",
              borderTop: "0.5px solid var(--color-border-faint, #f1f3f4)",
            }}
          >
            {onDirectionsClick && (
              <ActionLink label="Directions" onClick={onDirectionsClick} />
            )}
            {onDirectionsClick && onWebsiteClick && SEP}
            {onWebsiteClick && (
              <ActionLink label="Website" onClick={onWebsiteClick} />
            )}
            {(onDirectionsClick || onWebsiteClick) && onCallClick && SEP}
            {onCallClick && <ActionLink label="Call" onClick={onCallClick} />}
            {neighborhood && (
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "13px",
                  color: "var(--color-text-faint, #7a7f85)",
                }}
              >
                {neighborhood}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionLink({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: "14px",
        fontWeight: 500,
        color: "var(--color-text-muted, #4a4d51)",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 0,
        transition: "color 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = "var(--color-text, #1a1a1a)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = "var(--color-text-muted, #4a4d51)";
      }}
    >
      {label}
    </button>
  );
}
